import { useCallback, useRef } from 'react';
import { scrollEvents, ScrollSource } from './internal/scroll-event-bus';

/**
 * Scoped Element Search
 * 
 * WHY: Prevents finding wrong elements when duplicate IDs exist.
 * 
 * PROBLEM: When a modal is open, both the background page and modal render
 * the same address content with identical element IDs (e.g., "desktop-overview").
 * 
 * SOLUTION: Scope the search based on source:
 * - Modal: Search within [data-scroll-scope="modal"] container
 * - Window: Search globally with document.getElementById
 * 
 * This ensures we always find the element in the correct context.
 */
function getScopedElement(elementId: string, source: ScrollSource): HTMLElement | null {
  if (source === 'modal') {
    const modalContainer = document.querySelector('[data-scroll-scope="modal"]');
    return modalContainer?.querySelector(`#${elementId}`) as HTMLElement | null;
  }
  return document.getElementById(elementId);
}

/**
 * Tab Navigation Hook
 * 
 * WHY: Handles tab clicks and publishes scroll events with proper context.
 * 
 * RESPONSIBILITIES:
 * - Find the target element (scoped to source)
 * - Publish scroll event with source, offset, and completion callback
 * - Track scrolling state to prevent interference with IntersectionObserver
 * 
 * The hook doesn't perform scrolling itself - it delegates to the event bus.
 */
export function useTabNavigation(
  viewport: 'desktop' | 'mobile',
  source: ScrollSource,
  scrollOffset: number,
) {
  const isScrollingRef = useRef(false);

  const scrollToTab = useCallback((tabId: string) => {
    const elementId = `${viewport}-${tabId}`;
    const element = getScopedElement(elementId, source);
    if (!element) return;
    
    isScrollingRef.current = true;
    scrollEvents.publish(element, source, scrollOffset, () => {
      isScrollingRef.current = false;
    });
  }, [viewport, source, scrollOffset]);

  return { scrollToTab, isScrollingRef };
}
